/**
 * Before vs After — Interactive Comparison
 *
 * Desktop: drag the vertical slider (or click anywhere on the comparison)
 *          to reveal more/less of the BEFORE or AFTER state.
 *          Keyboard: ArrowLeft / ArrowRight to nudge the slider.
 *
 * Mobile:  swipe horizontally on the comparison area, or tap the
 *          BEFORE / AFTER label buttons, to switch panels.
 *
 * Videos:  IntersectionObserver pauses videos when the section leaves
 *          the viewport and resumes when it re-enters.
 *
 * Structure:
 *   .bva__content-wrap   (position: relative — shared parent)
 *     .bva__header        (BEFORE / AFTER text)
 *     .bva__comparison    (media panels, overflow: hidden)
 *     .bva__divider       (position: absolute — ONE continuous line + handle)
 *
 *   The divider lives as a sibling of header and comparison so its line
 *   runs continuously from header top to below the media. JS positions
 *   the handle at the vertical centre of the comparison.
 */

if (!customElements.get('before-vs-after')) {
  customElements.define(
    'before-vs-after',
    class BeforeVsAfter extends HTMLElement {
      constructor() {
        super();

        // ── State ──────────────────────────────────────────
        /** Current split percentage 0–100 (desktop) */
        this._split = 50;
        /** Whether the user is actively dragging */
        this._dragging = false;
        /** Whether the viewport is in mobile breakpoint */
        this._isMobile = false;
        /** Touch start X for swipe detection */
        this._touchStartX = 0;
        /** Touch start Y for swipe detection (used to detect vertical scroll) */
        this._touchStartY = 0;

        // Pre-bind handlers that need to be added/removed from document
        this._onMouseMove    = this._onMouseMove.bind(this);
        this._onMouseUp      = this._onMouseUp.bind(this);
        this._onDocTouchMove = this._onDocTouchMove.bind(this);
        this._onDocTouchEnd  = this._onDocTouchEnd.bind(this);
        this._onResize       = this._onResize.bind(this);
      }

      connectedCallback() {
        // ── Grab elements ──────────────────────────────────
        this._contentWrap   = this.querySelector('.bva__content-wrap');
        this._comparison    = this.querySelector('.bva__comparison');
        this._afterPanel    = this.querySelector('.bva__media--after');
        this._divider       = this.querySelector('.bva__divider');
        this._dividerHandle = this.querySelector('.bva__divider-handle');
        this._dotBtns       = this.querySelectorAll('.bva__dot-btn');
        this._mobileLabel   = this.querySelector('.bva__mobile-label');

        this._isMobile = window.innerWidth <= 749;

        this._setupDesktop();
        this._setupMobile();
        this._setupVideos();

        // Apply the initial 50/50 split on desktop
        if (!this._isMobile) {
          this._applyDesktopSplit(this._split);
          this._positionHandle();
        }

        window.addEventListener('resize', this._onResize, { passive: true });
      }

      disconnectedCallback() {
        // Clean up all global listeners
        document.removeEventListener('mousemove', this._onMouseMove);
        document.removeEventListener('mouseup', this._onMouseUp);
        document.removeEventListener('touchmove', this._onDocTouchMove);
        document.removeEventListener('touchend', this._onDocTouchEnd);
        window.removeEventListener('resize', this._onResize);
        if (this._videoObserver) this._videoObserver.disconnect();
      }

      // ──────────────────────────────────────────────────────
      // HANDLE POSITIONING
      // ──────────────────────────────────────────────────────

      /**
       * Position the drag handle at the vertical centre of the
       * comparison area within the content-wrap.
       *
       * The divider spans the full content-wrap height (header + media)
       * plus a 22 px extension below. The handle must sit at the
       * vertical midpoint of just the comparison, not the whole wrapper.
       */
      _positionHandle() {
        if (!this._contentWrap || !this._comparison || !this._dividerHandle) return;

        const wrapRect = this._contentWrap.getBoundingClientRect();
        const compRect = this._comparison.getBoundingClientRect();
        const centerY  = (compRect.top - wrapRect.top) + (compRect.height / 2);

        this._dividerHandle.style.top = `${centerY}px`;
      }

      // ──────────────────────────────────────────────────────
      // DESKTOP — drag slider
      // ──────────────────────────────────────────────────────

      _setupDesktop() {
        const { _divider: divider, _comparison: comp } = this;
        if (!divider || !comp) return;

        // ── Mouse drag ─────────────────────────────────────
        // mousedown fires on divider via event bubbling from handle
        // (handle has pointer-events: all, divider has pointer-events: none)
        divider.addEventListener('mousedown', (e) => {
          this._dragging = true;
          e.preventDefault(); // prevent text selection
          document.addEventListener('mousemove', this._onMouseMove);
          document.addEventListener('mouseup', this._onMouseUp);
        });

        // Clicking anywhere on the comparison instantly jumps the slider
        comp.addEventListener('click', (e) => {
          if (this._isMobile) return;
          this._updateSplitFromX(e.clientX);
        });

        // ── Keyboard accessibility ─────────────────────────
        divider.addEventListener('keydown', (e) => {
          if (this._isMobile) return;
          const step = 2; // percentage per keypress
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this._applyDesktopSplit(Math.max(0, this._split - step));
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this._applyDesktopSplit(Math.min(100, this._split + step));
          }
        });

        // ── Touch drag on divider (hybrid / stylus devices) ─
        divider.addEventListener('touchstart', () => {
          if (this._isMobile) return; // mobile uses swipe, not drag
          this._dragging = true;
          document.addEventListener('touchmove', this._onDocTouchMove, { passive: true });
          document.addEventListener('touchend', this._onDocTouchEnd);
        }, { passive: true });
      }

      _onMouseMove(e) {
        if (!this._dragging) return;
        this._updateSplitFromX(e.clientX);
      }

      _onMouseUp() {
        this._dragging = false;
        document.removeEventListener('mousemove', this._onMouseMove);
        document.removeEventListener('mouseup', this._onMouseUp);
      }

      _onDocTouchMove(e) {
        if (!this._dragging || this._isMobile) return;
        this._updateSplitFromX(e.touches[0].clientX);
      }

      _onDocTouchEnd() {
        this._dragging = false;
        document.removeEventListener('touchmove', this._onDocTouchMove);
        document.removeEventListener('touchend', this._onDocTouchEnd);
      }

      /**
       * Convert a raw clientX pixel into a 0–100 percentage within
       * the comparison container, then apply it.
       * @param {number} clientX
       */
      _updateSplitFromX(clientX) {
        const rect = this._comparison.getBoundingClientRect();
        const x    = clientX - rect.left;
        const pct  = Math.min(100, Math.max(0, (x / rect.width) * 100));
        this._applyDesktopSplit(pct);
      }

      /**
       * Apply the split:
       * 1. Clip the AFTER panel so only the left `pct`% is visible.
       * 2. Move the divider to the same horizontal position.
       * 3. Update the ARIA valuenow attribute.
       *
       * The divider is inside .bva__content-wrap which has the same
       * width as .bva__comparison, so left percentages align exactly.
       * @param {number} pct 0–100
       */
      _applyDesktopSplit(pct) {
        this._split = pct;
        this._afterPanel.style.clipPath = `inset(0 0 0 ${pct}%)`;
        this._divider.style.left        = `${pct}%`;
        this._divider.setAttribute('aria-valuenow', String(Math.round(pct)));
      }

      // ──────────────────────────────────────────────────────
      // MOBILE — horizontal swipe + label buttons
      // ──────────────────────────────────────────────────────

      _setupMobile() {
        const comp = this._comparison;
        if (!comp) return;

        // Record where the touch started
        comp.addEventListener('touchstart', (e) => {
          this._touchStartX = e.touches[0].clientX;
          this._touchStartY = e.touches[0].clientY;
        }, { passive: true });

        // On release: decide if it was a horizontal swipe
        comp.addEventListener('touchend', (e) => {
          if (!this._isMobile) return;

          const dx = e.changedTouches[0].clientX - this._touchStartX;
          const dy = e.changedTouches[0].clientY - this._touchStartY;

          // Ignore: too short, or more vertical than horizontal (user is scrolling)
          if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;

          // Swipe left → show AFTER  |  Swipe right → show BEFORE
          this._setMobilePanel(dx < 0 ? 'after' : 'before');
        }, { passive: true });

        // Dot buttons let users tap to switch panels too
        this._dotBtns.forEach((btn) => {
          btn.addEventListener('click', () => {
            this._setMobilePanel(btn.dataset.panel);
          });
        });
      }

      /**
       * Switch which panel is visible on mobile.
       * Updates the data attribute (CSS reads this), dots, and buttons.
       * @param {'before'|'after'} panel
       */
      _setMobilePanel(panel) {
        this.setAttribute('data-mobile-panel', panel);

        // Sync dot buttons
        this._dotBtns.forEach((btn) => {
          const active = btn.dataset.panel === panel;
          btn.classList.toggle('bva__dot-btn--active', active);
          btn.setAttribute('aria-pressed', String(active));
        });

        // Sync the label above the image
        if (this._mobileLabel) {
          const activeBtn = this.querySelector(`.bva__dot-btn[data-panel="${panel}"]`);
          this._mobileLabel.textContent = activeBtn
            ? activeBtn.getAttribute('aria-label')
            : panel;
        }
      }

      // ──────────────────────────────────────────────────────
      // VIDEOS — play/pause based on viewport visibility
      // ──────────────────────────────────────────────────────

      _setupVideos() {
        const videos = this.querySelectorAll('video');
        if (!videos.length) return;

        /**
         * Use IntersectionObserver so videos:
         * - Play automatically when the section enters view
         * - Pause when it scrolls out of view (saves CPU/battery)
         */
        this._videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              videos.forEach((video) => {
                if (entry.isIntersecting) {
                  video.play().catch(() => {
                    // Autoplay blocked — silently ignore
                  });
                } else {
                  video.pause();
                }
              });
            });
          },
          { threshold: 0.1 }
        );

        this._videoObserver.observe(this);
      }

      // ──────────────────────────────────────────────────────
      // RESIZE — handle breakpoint changes
      // ──────────────────────────────────────────────────────

      _onResize() {
        const wasMobile = this._isMobile;
        this._isMobile  = window.innerWidth <= 749;

        // Restore desktop clip-path when window grows back above breakpoint
        if (wasMobile && !this._isMobile) {
          this._applyDesktopSplit(this._split);
        }

        // Re-position handle on every resize (comparison height may change)
        if (!this._isMobile) {
          this._positionHandle();
        }
      }
    }
  );
}
